import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import iconUrl from 'leaflet/dist/images/marker-icon.png'
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png'
import iconShadowUrl from 'leaflet/dist/images/marker-shadow.png'
import styles from './ActivityMap.module.css'

// Leaflet's default marker icon resolves via relative URLs at runtime,
// which Vite's bundler breaks. Re-bind once at module load using the
// asset URLs imported above (which Vite hashes correctly).
const DefaultIcon = L.icon({
	iconUrl,
	iconRetinaUrl,
	shadowUrl: iconShadowUrl,
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	tooltipAnchor: [16, -28],
	shadowSize: [41, 41],
})
L.Marker.prototype.options.icon = DefaultIcon

type Props = {
	lat: number
	lng: number
	// When supplied, the marker is draggable and onChange fires after
	// each drag. Omit for a read-only map.
	onChange?: ((lat: number, lng: number) => void) | undefined
	zoom?: number | undefined
	className?: string | undefined
}

export const ActivityMap = ({
	lat,
	lng,
	onChange,
	zoom = 14,
	className,
}: Props) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const mapRef = useRef<L.Map | null>(null)
	const markerRef = useRef<L.Marker | null>(null)
	// Latest onChange in a ref so the marker handler doesn't capture
	// a stale closure when the parent re-renders with a new callback.
	const onChangeRef = useRef(onChange)
	onChangeRef.current = onChange

	// One-shot map setup. Lat/lng changes after mount flow through the
	// pan-and-move effect below rather than re-creating the map.
	useEffect(() => {
		if (!containerRef.current || mapRef.current) return

		const map = L.map(containerRef.current, {
			zoomControl: true,
			attributionControl: true,
		}).setView([lat, lng], zoom)

		L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
			maxZoom: 19,
			attribution: '© OpenStreetMap contributors',
		}).addTo(map)

		const marker = L.marker([lat, lng], {
			draggable: Boolean(onChangeRef.current),
		}).addTo(map)

		marker.on('dragend', () => {
			const ll = marker.getLatLng()
			onChangeRef.current?.(ll.lat, ll.lng)
		})

		mapRef.current = map
		markerRef.current = marker

		return () => {
			map.remove()
			mapRef.current = null
			markerRef.current = null
		}
		// Intentionally empty — initial position is read from props
		// once; subsequent updates go through the effect below.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Sync the marker (and pan the map) when the controlled lat/lng
	// changes from outside — e.g. user picks a different geocoder
	// result.
	useEffect(() => {
		const map = mapRef.current
		const marker = markerRef.current
		if (!map || !marker) return
		marker.setLatLng([lat, lng])
		map.setView([lat, lng], map.getZoom())
	}, [lat, lng])

	return (
		<div
			ref={containerRef}
			className={`${styles.map} ${className ?? ''}`.trim()}
		/>
	)
}
